import { ExperienceEventType } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { useUnsafeTranslation } from '../../i18next';
import { useRoutes } from '../../routes';
import { useAdminUsersInOrg } from '../../services/adminUsers';
import {
  isDadoEmailAddress,
  useIsCurrentUserDadoEmployee
} from '../../services/currentUser';
import { Timestamp, toMoment } from '../../services/time';
import { toFullName } from '../../services/user';
import { DateTimeHoverTooltip } from '../DateTimeTooltip';
import { Date } from '../Dates';
import { LinkExternal } from '../LinkExternal';

const LogGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gridColumnGap: p.theme.spacing(3),
  gridRowGap: p.theme.spacing(2)
}));

const LogSubText = styled('span')((p) => ({
  color: p.theme.palette.text.hint
}));

export type LogItem = {
  key?: string;
  at: Timestamp;
  d: React.ReactNode;
  executor:
    | {
        type: 'user';
        userId: string;
      }
    | {
        type: 'import';
        importId: string;
      }
    | null;
  searchValue: string;
  actor: string | 'AUTOMATION' | 'OTHER' | null;
  type: ExperienceEventType; // That's all they are right now. Might relax this to other things eentually
};

const isSameDay = (a: Timestamp, b: Timestamp, tz?: string) => {
  if (tz) {
    return toMoment(a).tz(tz).isSame(toMoment(b).tz(tz), 'd');
  }
  return toMoment(a).isSame(toMoment(b), 'd');
};

const AdditionalInfo = ({
  d,
  orgId,
  tz
}: {
  d: LogItem;
  orgId: string;
  tz?: string;
}) => {
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const [adminUsers] = useAdminUsersInOrg(orgId);
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  const additionalSubText = useMemo(() => {
    if (!d.executor) {
      return null;
    }
    if (d.executor.type === 'user' && d.executor.userId) {
      if (!adminUsers) {
        return null;
      }
      const userId = d.executor.userId;
      const user = adminUsers.find((a) => a.id === userId);
      if (!user) {
        return null;
      }
      const isDadoUser = isDadoEmailAddress(user.data.email);
      if (isDadoUser && !isDadoEmployee) {
        return 'Dado Customer Support';
      }
      return toFullName(user.data);
    }
    if (d.executor.type === 'import' && d.executor.importId) {
      return (
        <span>
          {t('via')}{' '}
          <LinkExternal
            color="primary"
            href={ROUTES.users.imports.details.url(d.executor.importId)}
            // down below "import" is not wrapped in t - that's on purpose,
            // because it turns out that twe word import cannot be used as a key!!
          >
            import
          </LinkExternal>
        </span>
      );
    }
    return null;
  }, [adminUsers, d.executor, isDadoEmployee, t, ROUTES]);
  const time = tz
    ? toMoment(d.at).tz(tz).format('h:mm A z')
    : toMoment(d.at).format('HH:mm');
  const timeNode = tz ? <DateTimeHoverTooltip at={d.at}>{time}</DateTimeHoverTooltip> : time;

  if (additionalSubText) {
    return (
      <LogSubText>
        ({timeNode}, {additionalSubText})
      </LogSubText>
    );
  }
  return <LogSubText>({timeNode})</LogSubText>;
};

export const LogTable = ({
  orgId,
  ds,
  tz
}: {
  orgId: string;
  ds: LogItem[];
  tz?: string;
}) => {
  return (
    <LogGrid>
      {ds.map((item, i) => {
        const prev = ds[i - 1];
        const showDate = i === 0 || (prev && !isSameDay(prev.at, item.at, tz));
        return (
          <React.Fragment key={item.key || i}>
            <LogSubText>
              {showDate &&
                (tz ? toMoment(item.at).tz(tz).format('D MMM YYYY') : <Date d={item.at} />)}
            </LogSubText>
            <div>
              {item.d} <AdditionalInfo d={item} orgId={orgId} tz={tz} />
            </div>
          </React.Fragment>
        );
      })}
    </LogGrid>
  );
};
